import { trigger, transition, style, animate } from '@angular/animations';
import { Component, inject, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { SharedService } from '@shared/services/shared.service';
import { IInHomeCareChange } from 'app/models/licenses/in-home-care/in-home-care-change';
import { ILicenseRequest } from 'app/models/licenses/license-request';
import { InHomeCareChangeService } from 'app/services/license/in-home-care/in-home-care-change.service';
import { InHomeCareLicenseService } from 'app/services/license/in-home-care/in-home-care-license.service';

interface IClaimAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

@Component({
  selector: 'app-in-home-care-claims',
  standalone: false,
  templateUrl: './in-home-care-claims.component.html',
  styleUrl: './in-home-care-claims.component.scss',
  animations: [
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})

export class InHomeCareClaimsComponent {
  public requests: ILicenseRequest[] = [];
  public request: ILicenseRequest = {
    id: 0,
    owner: '',
    licenseNumber: '',
    email: '',
    requestData: '',
    parentId: '',
    ownerChange: false,
    approved: false,
    denied: false,
    deniedReason: '',
    finalDeterminationBy: '',
    finalDeterminationDate: ''
  };
  public claimAddress: IClaimAddress = {
    street: '',
    city: '',
    state: '',
    zip: ''
  };
  public ownerChange: IInHomeCareChange = {
    newParent: '',
    subunits: [],
    id: 0,
    licenseId: 0,
    licenseGuid: '',
    entityName: '',
    agencyDBAName: '',
    entity: '',
    type: '',
    city: '',
    originalValue: '',
    newValue: '',
    dateRequested: '',
    requestedBy: '',
    effectiveDateOfChange: '',
    signature: '',
    title: '',
    dateSigned: '',
    requiresPayment: false,
    assignedToId: '',
    assignedToName: '',
    isRenewal: false,
    approved: false,
    denied: false,
    determinationBy: '',
    dateOfDetermination: ''
  }
  public requestStatus: string = '';
  public reason: string = '';
  public showAll: boolean = false;
  public ownerClaim: boolean = false;
  public requestColumns: string [] = ['owner', 'licenseNumber', 'email', 'status'];
  public requestSource = new MatTableDataSource<ILicenseRequest>(this.requests);
  @ViewChild('requestPaginator', {static: false}) requestPaginator!: MatPaginator;
  @ViewChild('requestSort', {static: false}) requestSort!: MatSort;

  public licenseService: InHomeCareLicenseService = inject(InHomeCareLicenseService);
  public inHomeCareChangeService: InHomeCareChangeService = inject(InHomeCareChangeService);
  public sharedService: SharedService = inject(SharedService);
  public router = inject(Router);

  constructor() {
    this.ownerClaim = this.router.url.includes('ownerClaims');
  }

  ngOnInit(): void {
    this.getLicenseRequests(this.showAll);
  }

  getLicenseRequests(includeAll: boolean): void {
    this.showAll = includeAll;
    this.licenseService.getLicenseRequests(this.ownerClaim, includeAll).subscribe({
      next: response => this.requests = response,
      error: error => console.error(error),
      complete: () => this.updateRequestTable()
    });
  }

  approveRequest(approved: boolean): void {
    this.sharedService.openConfirm((approved? "Approve " : "Deny ") + "this request?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.request.approved = approved;
          this.request.denied = !approved;
          this.request.deniedReason = this.reason;

          this.licenseService.approveLicenseRequest(this.request).subscribe({
            complete: () => {
              this.requests = this.requests.filter(r => r.id !== this.request.id);
              this.updateRequestTable();
              this.request = this.getEmptyRequest();
              this.reason = '';
            },
            error: error => console.error(error)
          });
        }
      }
    );
  }

  updateRequestTable(): void {
    setTimeout(() => {
      this.requestSource.data = this.requests;
      this.requestSource.sort = this.requestSort;
      this.requestSource.paginator = this.requestPaginator;
    }, 500);
  }

  filterRequests(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.requestSource.filter = filterValue.trim().toLowerCase();
  }

  selectRequest(request: ILicenseRequest): void {
    this.request = request;
    this.claimAddress = JSON.parse(this.request.requestData);
    this.checkRequestStatus();
  }

  checkRequestStatus(): void {
    this.licenseService.checkLicenseRequest(this.request.licenseNumber).subscribe({
      next: response => this.requestStatus = response,
      error: error => console.error(error),
      complete: () => {
        if(this.ownerClaim){
          this.inHomeCareChangeService.getOwnerChange(this.request.licenseNumber).subscribe({
            next: response => this.ownerChange = response,
            error: error => console.log('error getting change info', error)
          });
        }
      }
    });
  }

  close(): void {
    this.request = this.getEmptyRequest();
    this.reason = '';
    this.requestStatus = '';
    this.ownerChange = this.getEmptyChange();
    this.claimAddress = {
      street: '',
      city: '',
      state: '',
      zip: ''
    };
    this.updateRequestTable();
  }

  getEmptyRequest(): ILicenseRequest {
    return {
      id: 0,
      owner: '',
      licenseNumber: '',
      email: '',
      requestData: '',
      parentId: '',
      ownerChange: false,
      approved: false,
      denied: false,
      deniedReason: '',
      finalDeterminationBy: '',
      finalDeterminationDate: ''
    }
  }

  getEmptyChange(): IInHomeCareChange {
    return {
      newParent: '',
      subunits: [],
      id: 0,
      licenseId: 0,
      licenseGuid: '',
      entityName: '',
      agencyDBAName: '',
      entity: '',
      type: '',
      city: '',
      originalValue: '',
      newValue: '',
      dateRequested: '',
      requestedBy: '',
      effectiveDateOfChange: '',
      signature: '',
      title: '',
      dateSigned: '',
      requiresPayment: false,
      assignedToId: '',
      assignedToName: '',
      isRenewal: false,
      approved: false,
      denied: false,
      determinationBy: '',
      dateOfDetermination: ''
    }
  }
}
